import { createContext, useContext } from 'react';
// Type-only imports erase at build, so this neutral module carries NO runtime
// dependency on the tenant feature's axios singleton. Both the tenant app
// (TenantProvider) and the admin package workspace (PackageApiProvider) inject a
// conforming implementation, so the throwing default below is never reached at
// runtime. [FE-MNT-03]
import type { quickReplyApi } from '../../features/templates/services/quickReplyApi';
import type { templatesApi } from '../../features/templates/services/templatesApi';

export type QuickReplyApi = typeof quickReplyApi;
export type WhatsAppTemplateApi = typeof templatesApi;

/**
 * Services + scope key consumed by the tenant template hooks/pages. Both
 * `/v1/*` (tenant) and `/admin/api/packages/{p}/draft/*` (admin package
 * workspace) inject conforming implementations.
 *
 * `scope` is a react-query cache-key segment. Any two consumers with
 * different scopes have independent caches — crucial so the admin package
 * workspace's draft items don't leak into the tenant app's cache.
 */
export interface TemplateApiServices {
  quickReply: QuickReplyApi;
  waTemplate: WhatsAppTemplateApi;
  scope: string;
  /**
   * When true, source='system' rows are NOT treated as read-only. Admin's
   * package-draft workspace sets this so authors can edit/delete the templates
   * they author; tenant mounts leave it false so end-users can't tamper with
   * system-deployed rows.
   */
  allowSystemEdit?: boolean;
}

// No-axios default. Every real mount supplies a provider value; this stub only
// guards against accidentally consuming the context outside a provider.
const missingProvider = () => {
  throw new Error('TemplateApiContext used without a provider');
};

const defaultServices: TemplateApiServices = {
  get quickReply(): QuickReplyApi {
    return missingProvider();
  },
  get waTemplate(): WhatsAppTemplateApi {
    return missingProvider();
  },
  scope: 'unscoped',
  allowSystemEdit: false,
};

export const TemplateApiContext = createContext<TemplateApiServices>(defaultServices);

export function useTemplateApi(): TemplateApiServices {
  return useContext(TemplateApiContext);
}
